import { useState } from "react";
import { FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { downloadFile, blobError } from "@/components/ExportRunMenu";
import { todayISO } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/DatePicker";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const ExportRunsDialog = ({ open, onOpenChange, locksmiths, initial }) => {
  const [f, setF] = useState({ from: initial.from || todayISO(), to: initial.to || todayISO(), locksmithId: initial.locksmithId || "all" });
  const [busy, setBusy] = useState(false);
  const invalid = !f.from || !f.to || f.from > f.to;

  const submit = async () => {
    setBusy(true);
    try {
      await downloadFile("/exports/runs", { from: f.from, to: f.to, locksmith_id: f.locksmithId }, "warrant-runs.xlsx");
      toast.success("Spreadsheet downloaded");
      onOpenChange(false);
    } catch (e) {
      toast.error(await blobError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="export-runs-dialog">
        <DialogHeader>
          <DialogTitle>Export warrant runs</DialogTitle>
          <DialogDescription>Download every run in the date range as one Excel spreadsheet, one row per address.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5"><Label htmlFor="exp-from">From</Label>
            <DatePicker id="exp-from" value={f.from} onChange={(v) => setF({ ...f, from: v })} testId="export-runs-from" /></div>
          <div className="space-y-1.5"><Label htmlFor="exp-to">To</Label>
            <DatePicker id="exp-to" value={f.to} onChange={(v) => setF({ ...f, to: v })} testId="export-runs-to" /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Locksmith</Label>
            <Select value={f.locksmithId} onValueChange={(v) => setF({ ...f, locksmithId: v })}>
              <SelectTrigger data-testid="export-runs-locksmith"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All locksmiths</SelectItem>
                {initial.locksmithId === "open" && <SelectItem value="open">Open jobs (not taken yet)</SelectItem>}
                {locksmiths.map((l) => <SelectItem key={l.id} value={l.id}>{l.full_name || l.email}</SelectItem>)}
              </SelectContent>
            </Select></div>
        </div>
        {f.from && f.to && f.from > f.to && <p className="text-sm text-rose-600" data-testid="export-runs-error">From date must be on or before the to date.</p>}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="export-runs-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || invalid} className="bg-blue-700 hover:bg-blue-800" data-testid="export-runs-submit">
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-1 h-4 w-4" />} Download Excel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
